/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useState } from "react";
import ProfileSectionAction from "./ProfileSectionAction";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

export default function ProfileCollections({
  isOwnProfile,
  collections,
  setCollections,
  collectionIntro,
  setCollectionIntro,
  collectionIntroOpen,
  setCollectionIntroOpen,
  collectionMeta,
  setCollectionMeta,
  editingCollection,
  setEditingCollection,
  editTitle,
  setEditTitle,
  editDescription,
  setEditDescription,
  setCollectionPhotos,
  setCollectionIndex,
  setCollectionName,
  setCollectionViewOpen,
  selectedSpeciesForCollection,
  setSelectedSpeciesForCollection,
  selectedPhotoForCollection,
  setSelectedPhotoForCollection,
  collectionSelectorOpen,
  setCollectionSelectorOpen,
  confirmAddOpen,
  setConfirmAddOpen,
  photoAddedMessage,
  setPhotoAddedMessage,
  speciesWithPhotos,
  SectionRefreshOverlay,
  sectionLoading,
  sectionProgress,
  showCollectionsHint,
  setShowCollectionsHint,
}) {
  const [createCollectionOpen, setCreateCollectionOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [newCollectionDescription, setNewCollectionDescription] = useState("");
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [createCollectionError, setCreateCollectionError] = useState("");

  function closeCreateCollection() {
    if (creatingCollection) return;

    setCreateCollectionOpen(false);
    setNewCollectionName("");
    setNewCollectionDescription("");
    setCreateCollectionError("");
  }

  async function createCollection() {
    if (creatingCollection || !auth.currentUser) return;

    const name = newCollectionName.trim();
    const description = newCollectionDescription.trim();

    if (!name) {
      setCreateCollectionError("Please enter a collection name.");
      return;
    }

    const collectionAlreadyExists = [
      ...Object.keys(collections),
      ...Object.keys(collectionMeta),
    ].some(
      (existingName) =>
        existingName.trim().toLowerCase() === name.toLowerCase(),
    );

    if (collectionAlreadyExists) {
      setCreateCollectionError("A collection with this name already exists.");
      return;
    }

    setCreatingCollection(true);
    setCreateCollectionError("");

    try {
      const collectionData = {
        title: name,
        description,
      };

      await setDoc(
        doc(db, "users", auth.currentUser.uid, "collectionsMeta", name),
        collectionData,
        { merge: true },
      );

      setCollections((prev) => ({
        ...prev,
        [name]: [],
      }));

      setCollectionMeta((prev) => ({
        ...prev,
        [name]: collectionData,
      }));

      setCreateCollectionOpen(false);
      setNewCollectionName("");
      setNewCollectionDescription("");
    } catch (error) {
      console.error("Failed to create collection:", error);
      setCreateCollectionError(
        "The collection could not be created. Please try again.",
      );
    } finally {
      setCreatingCollection(false);
    }
  }

  return (
    <>
      {/* COLLECTIONS */}
      <div className="relative bg-white rounded-3xl shadow-lg border border-[#93ca87]/40 p-8">
        <SectionRefreshOverlay
          show={sectionLoading.collections}
          progress={sectionProgress.collections}
          label="Refreshing collections"
        />

        <div className="mb-5 sm:mb-7">
          <div className="bioindex-section-heading">
            <h2
              className="text-3xl leading-none text-[#374049] sm:text-4xl"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Collections
            </h2>
            {isOwnProfile && (
              <div className="bioindex-section-actions" aria-label="Collection actions">
                <ProfileSectionAction
                  kind="info"
                  label="About Collections"
                  active={showCollectionsHint}
                  onClick={() => setShowCollectionsHint((prev) => !prev)}
                />
                <ProfileSectionAction
                  kind="add"
                  label="Create Collection"
                  onClick={() => {
                    setCreateCollectionError("");
                    setCreateCollectionOpen(true);
                  }}
                />
              </div>
            )}
          </div>

          {showCollectionsHint && (
            <div className="bioindex-section-hint" role="note">
              Create themed collections for locations, projects, and other
              groups of observations. Add photos during upload or from an
              existing collection, Explore Taxonomy, or Search.
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(collections).map(([name, items]) => (
            <div
              key={name}
              onClick={() => {
                setCollectionPhotos([...(items || [])]);
                setCollectionIndex(0);
                setCollectionName(name);
                setCollectionViewOpen(true);
              }}
              className="group relative mx-auto w-full max-w-[15.5rem] cursor-pointer"
            >
              <div className="relative aspect-[5/6] overflow-hidden rounded-r-2xl rounded-l-md border border-black/30 bg-[#2f3941] shadow-[0_18px_35px_rgba(0,0,0,0.22)] transition duration-300 group-hover:-translate-y-[3px] group-hover:rotate-[0.5deg] group-hover:shadow-[0_24px_45px_rgba(0,0,0,0.28)]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_35%_18%,rgba(255,255,255,0.10),transparent_28%),radial-gradient(circle_at_65%_78%,rgba(0,0,0,0.20),transparent_34%)]" />
                <div className="absolute inset-0 opacity-[0.18] [background-image:repeating-linear-gradient(35deg,rgba(255,255,255,0.18)_0px,rgba(255,255,255,0.18)_1px,transparent_1px,transparent_5px)]" />

                <div className="absolute inset-4 rounded-r-xl border border-black/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),inset_0_0_0_1px_rgba(255,255,255,0.05)]" />

                <div className="absolute left-0 top-0 h-full w-8 border-r border-black/40 bg-[#1f282f] shadow-[inset_-5px_0_8px_rgba(0,0,0,0.35)]" />
                <div className="absolute left-3 top-0 h-full w-px bg-white/12" />
                <div className="absolute left-6 top-0 h-full w-px bg-black/45" />

                <div className="absolute right-0 top-5 h-[calc(100%-2.5rem)] w-1 rounded-r-xl bg-black/25" />

                <div className="absolute right-5 top-5 h-9 w-9 rounded-br-xl border-b border-r border-black/25 opacity-50" />
                <div className="absolute bottom-5 right-5 h-9 w-9 rounded-tl-xl border-l border-t border-black/25 opacity-50" />
                <div className="absolute left-1/2 top-8 w-[68%] aspect-square -translate-x-[42%] rounded-xl bg-[#151c22] p-[11px] shadow-[inset_0_7px_13px_rgba(0,0,0,0.82),inset_0_-2px_0_rgba(255,255,255,0.07),0_1px_0_rgba(255,255,255,0.08)]">
                  <div className="pointer-events-none absolute inset-[3px] rounded-xl border border-black/60 shadow-[inset_0_2px_3px_rgba(255,255,255,0.08),inset_0_-3px_5px_rgba(0,0,0,0.28)]" />
                  <div className="relative h-full w-full overflow-hidden rounded-md bg-[#FCFFF6] shadow-[0_2px_4px_rgba(0,0,0,0.45)]">
                    {items.length >= 9 ? (
                      <div className="grid h-full w-full grid-cols-3 grid-rows-3 gap-[2px] bg-[#1f282f]">
                        {items.slice(0, 9).map((img, i) => (
                          <img
                            key={i}
                            src={img?.url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ))}
                      </div>
                    ) : items[0]?.url ? (
                      <img
                        src={items[0].url}
                        alt={name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[#FCFFF6] text-sm text-slate-400">
                        Empty
                      </div>
                    )}
                  </div>
                  <div className="pointer-events-none absolute inset-[9px] rounded-md shadow-[inset_0_3px_7px_rgba(0,0,0,0.45)]" />
                </div>

                <div className="absolute bottom-8 left-1/2 w-[60%] -translate-x-[42%]">
                  <div
                    className="relative mx-auto min-h-[2.05rem] rounded-[2px] border border-[#6f6658]/75 bg-[#2b2d36]/85 px-3 py-[3px] text-center shadow-[inset_0_1px_2px_rgba(0,0,0,0.7),inset_0_-1px_0_rgba(255,255,255,0.07)]"
                    style={{ fontFamily: "Cormorant Garamond" }}
                  >
                    <div className="pointer-events-none absolute inset-[3px] border border-[#FCFFF6]/8" />

                    <div className="mx-auto line-clamp-2 flex min-h-[1.65rem] items-center justify-center text-center text-sm leading-[1.05] text-[#FCFFF6]">
                      {collectionMeta[name]?.title || name}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {createCollectionOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/45 px-3 pb-6 pt-12 sm:items-center sm:px-4 sm:pt-20"
          onClick={closeCreateCollection}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-3xl border border-[#93ca87]/40 bg-[#FCFFF6] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[#93ca87]/25 bg-[#374049] px-6 py-5 text-center">
              <h3
                className="text-3xl leading-none text-white"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Create Collection
              </h3>

              <p className="mt-2 text-xs text-white/65">
                Create an album for a location, project, trip, or theme.
              </p>
            </div>

            <div className="space-y-5 p-6">
              <div>
                <label
                  htmlFor="new-collection-name"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#374049]/70"
                >
                  Collection name
                </label>

                <input
                  id="new-collection-name"
                  type="text"
                  autoFocus
                  value={newCollectionName}
                  onChange={(e) => {
                    setNewCollectionName(e.target.value);
                    setCreateCollectionError("");
                  }}
                  maxLength={100}
                  placeholder="e.g., Los Tuxtlas Plants"
                  className="block w-full rounded-xl border border-[#93ca87]/50 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition placeholder:text-[#374049]/30 focus:border-[#24c3be] focus:ring-2 focus:ring-[#24c3be]/20"
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <label
                    htmlFor="new-collection-description"
                    className="text-xs font-semibold uppercase tracking-wide text-[#374049]/70"
                  >
                    Description
                  </label>

                  <span
                    className={`text-[10px] ${
                      newCollectionDescription.length >= 300
                        ? "font-semibold text-[#01767a]"
                        : "text-[#374049]/40"
                    }`}
                  >
                    {newCollectionDescription.length}/300
                  </span>
                </div>

                <textarea
                  id="new-collection-description"
                  value={newCollectionDescription}
                  onChange={(e) => setNewCollectionDescription(e.target.value)}
                  maxLength={300}
                  rows={5}
                  placeholder="Describe the purpose, location, project, or theme of this collection."
                  className="block w-full resize-none rounded-xl border border-[#93ca87]/50 bg-white px-4 py-3 text-sm leading-relaxed text-[#374049] outline-none transition placeholder:text-[#374049]/30 focus:border-[#24c3be] focus:ring-2 focus:ring-[#24c3be]/20"
                />
              </div>

              {createCollectionError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-center text-xs text-red-600">
                  {createCollectionError}
                </div>
              )}

              <div className="flex justify-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeCreateCollection}
                  disabled={creatingCollection}
                  className="rounded-md border border-[#374049]/20 bg-white px-5 py-2 text-xs font-medium text-[#374049] transition hover:bg-[#374049]/5 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={createCollection}
                  disabled={creatingCollection || !newCollectionName.trim()}
                  className="rounded-md border border-[#24c3be] bg-[#24c3be] px-5 py-2 text-xs font-medium text-white shadow-sm transition hover:bg-[#1faeaa] disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300"
                >
                  {creatingCollection ? "Creating..." : "Create Collection"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {collectionSelectorOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-start sm:items-center justify-center z-[60] px-3 sm:px-4 pt-12 sm:pt-20 pb-6 overflow-y-auto">
          <div className="bg-white rounded-2xl p-8 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            {!selectedSpeciesForCollection ? (
              <>
                <h3 className="text-lg font-semibold mb-4">Select Species</h3>

                <div className="space-y-2">
                  {speciesWithPhotos.map((sp) => (
                    <div
                      key={sp.id}
                      onClick={() => setSelectedSpeciesForCollection(sp)}
                      className="p-3 border rounded-xl cursor-pointer hover:bg-slate-50"
                    >
                      <span className="italic">{sp.name}</span>
                      {sp.common ? ` (${sp.common})` : ""}
                    </div>
                  ))}
                </div>

                <div className="mt-6 text-right">
                  <button
                    onClick={() => setCollectionSelectorOpen(false)}
                    className="px-4 py-2 border rounded-xl"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4">
                  Select Photo from {selectedSpeciesForCollection.name}
                </h3>

                <div className="grid grid-cols-3 gap-4">
                  {selectedSpeciesForCollection.forms.map((f) => (
                    <img
                      key={f.url}
                      src={f.url}
                      alt=""
                      onClick={() => {
                        setSelectedPhotoForCollection(f);
                        setConfirmAddOpen(true);
                      }}
                      className={`cursor-pointer rounded-xl object-cover h-32 w-full hover:opacity-80 border-2 ${
                        selectedPhotoForCollection?.url === f.url
                          ? "border-[#24c3be]"
                          : "border-transparent"
                      }`}
                    />
                  ))}
                </div>

                <div className="mt-6 text-right">
                  <button
                    onClick={() => setSelectedSpeciesForCollection(null)}
                    className="px-4 py-2 border rounded-xl"
                  >
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {confirmAddOpen && selectedPhotoForCollection && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center">
            {!photoAddedMessage ? (
              <>
                <p className="text-lg mb-4">
                  Confirm add to {collectionIntro.name}?
                </p>

                <div className="flex justify-center gap-3">
                  <button
                    onClick={async () => {
                      const updatedForms =
                        selectedSpeciesForCollection.forms.map((form) => {
                          if (form.url === selectedPhotoForCollection.url) {
                            const collections = form.collections || [];
                            if (!collections.includes(collectionIntro.name)) {
                              return {
                                ...form,
                                collections: [
                                  ...collections,
                                  collectionIntro.name,
                                ],
                              };
                            }
                          }
                          return form;
                        });

                      await setDoc(
                        doc(
                          db,
                          "users",
                          auth.currentUser.uid,
                          "species",
                          selectedSpeciesForCollection.id,
                        ),
                        { forms: updatedForms },
                        { merge: true },
                      );

                      setPhotoAddedMessage(true);
                    }}
                    className="px-4 py-2 bg-[#24c3be] text-white rounded-xl"
                  >
                    Yes
                  </button>

                  <button
                    onClick={() => {
                      setConfirmAddOpen(false);
                      setSelectedPhotoForCollection(null);
                    }}
                    className="px-4 py-2 border rounded-xl"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-lg mb-4">
                  Photo added to {collectionIntro.name}!
                </p>

                <button
                  onClick={() => {
                    setConfirmAddOpen(false);
                    setSelectedPhotoForCollection(null);
                    setSelectedSpeciesForCollection(null);
                    setCollectionSelectorOpen(false);
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-[#24c3be] text-white rounded-xl"
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {collectionIntroOpen && collectionIntro && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setCollectionIntroOpen(false)}
        >
          <div
            className="bg-white rounded-3xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {collectionIntro.items.length >= 9 ? (
              <div className="grid grid-cols-3 grid-rows-3 gap-1 mb-6 overflow-hidden rounded-2xl">
                {collectionIntro.items.slice(0, 9).map((img, i) => (
                  <img
                    key={i}
                    src={img?.url}
                    alt=""
                    className="w-full aspect-square object-cover"
                  />
                ))}
              </div>
            ) : (
              <div className="w-full mb-6 overflow-hidden rounded-2xl flex justify-center">
                {collectionIntro.items[0]?.url ? (
                  <img
                    src={collectionIntro.items[0].url}
                    alt={collectionIntro.name}
                    className="max-w-full max-h-[45vh] object-contain block"
                  />
                ) : (
                  <div className="w-full h-40 flex items-center justify-center text-sm text-slate-400 bg-slate-100">
                    Empty
                  </div>
                )}
              </div>
            )}

            {editingCollection ? (
              <div className="mb-6 space-y-3">
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full text-center text-xl border-b outline-none"
                />

                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Add description..."
                  className="w-full text-center text-sm border rounded-lg p-2 outline-none"
                />

                <div className="flex justify-center gap-2 mt-2">
                  <button
                    onClick={async () => {
                      const updated = {
                        title: editTitle,
                        description: editDescription,
                      };

                      setCollectionMeta((prev) => ({
                        ...prev,
                        [collectionIntro.name]: updated,
                      }));

                      await setDoc(
                        doc(
                          db,
                          "users",
                          auth.currentUser.uid,
                          "collectionsMeta",
                          collectionIntro.name,
                        ),
                        updated,
                        { merge: true },
                      );

                      setEditingCollection(false);
                    }}
                    className="px-4 py-1 bg-[#24c3be] text-white rounded-lg"
                  >
                    Save
                  </button>

                  <button
                    onClick={() => setEditingCollection(false)}
                    className="px-4 py-1 border rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl text-center text-[#374049]">
                  {collectionMeta[collectionIntro.name]?.title ||
                    collectionIntro.name}
                </h2>

                <p className="text-center text-slate-500 mt-2 mb-6">
                  {collectionMeta[collectionIntro.name]?.description ||
                    "No description"}
                </p>
              </>
            )}

            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  setCollectionPhotos([...(collectionIntro.items || [])]);
                  setCollectionIndex(0);
                  setCollectionName(collectionIntro.name);
                  setCollectionViewOpen(true);
                  setCollectionIntroOpen(false);
                }}
                className="px-5 py-2 bg-[#24c3be] text-white rounded-xl"
              >
                View Collection
              </button>

              {isOwnProfile && (
                <>
                  <button
                    onClick={() => {
                      setEditTitle(
                        collectionMeta[collectionIntro.name]?.title ||
                          collectionIntro.name,
                      );
                      setEditDescription(
                        collectionMeta[collectionIntro.name]?.description || "",
                      );
                      setEditingCollection(true);
                    }}
                    className="px-5 py-2 border border-slate-300 rounded-xl"
                  >
                    Edit Description
                  </button>

                  <button
                    onClick={() => {
                      setCollectionIntroOpen(false);
                      setSelectedSpeciesForCollection(null);
                      setCollectionSelectorOpen(true);
                    }}
                    className="px-5 py-2 border border-[#24c3be] text-[#24c3be] rounded-xl"
                  >
                    Add Photo
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
